import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import { nanoid } from 'nanoid';
import { insertSelectedPicklistItem } from '../RichTextEditor/plugins/picklist';
import { buildPicklistOptions } from '../RichTextEditor/plugins/picklist/utils';
import type {
  ReportPicklistOption,
  ReportPicklist,
  GetMacroQuery,
  Macro,
  ReportTemplateFieldsFragment,
} from 'generated/graphql';
import { sendCrossTabEvent, NameSpaces } from 'modules/EventsManager';
import { unfurlMacroPlaceholderStatic } from '../RichTextEditor/plugins/macroPlaceholder/useMacroPlaceholder';
import { logger } from 'modules/logger';
import { partialEditor } from '../RichTextEditor/utils';
import { ReactEditor, NodeEntry, useSlateStatic, useSlateSelection } from '../RichTextEditor/core';
import { useRequiredFieldIndicator } from 'hooks/useRequiredFieldIndicator';
import type { ReportMacros } from 'hooks/useCurrentCaseReport';
import {
  getPicklistFromClipboard,
  storeClipboardPicklists,
  getStoredPicklists,
} from './picklistClipboardStore';
import equals from 'ramda/es/equals';
import { getActivePicklist } from '../RichTextEditor/utils/selection';
import type { PicklistPluginElement } from '../RichTextEditor/slate-custom-types';
import { usePlugins } from '../RichTextEditor';
import type {
  PickListInsertionContext,
  PickListInsertionStart,
} from '../RichTextEditor/analytics/performance';
import { pickListInsertionMetrics } from '../RichTextEditor/analytics/performance';
export type Picklist = ReportPicklist;
export type Picklists = ReadonlyArray<ReportPicklist>;

// TODO: RP-3211 - Fix Macros types
export type Macros =
  | ReportMacros
  | GetMacroQuery['macro']['nestedMacros']
  | Array<Macro>
  | ReportTemplateFieldsFragment['macros'];

export type SelectedPicklistOptionsMap = {
  [key: string]: string;
};

export type PicklistOption = Readonly<{
  pickIndex: number;
  name: string;
}>;

type PicklistBagInternal = Readonly<{
  picklists: Picklists;
  insertPicklist: (arg1: { readonly picklistID: string }) => void;
  copyPicklist: (arg1: { readonly picklistID: string }) => Picklist;
  removePicklist: (arg1: { readonly picklistID: string }) => void;
  updatePicklist: (arg1: {
    readonly picklistID: string;
    options: Array<ReportPicklistOption>;
  }) => void;
  setPicklists: (arg1: Picklists | ((arg1: Picklists) => Picklists)) => void;
  selectedPicklistOptionIds: SelectedPicklistOptionsMap;
  setSelectedPicklistOptionIds: Dispatch<SetStateAction<SelectedPicklistOptionsMap>>;
}>;

type PicklistStoreBag = PicklistBagInternal & {
  setMacros: Dispatch<SetStateAction<Macros>>;
  macros: Macros;
  cacheClipboardPicklists: (picklistIDs: readonly string[]) => void;
};

type PicklistEditorBag = PicklistStoreBag & {
  setActivePicklist: (arg1: Nullable<Picklist>) => void;
  activePicklist: Nullable<Picklist>;
  activePicklistOptions: ReadonlyArray<PicklistOption>;
  getPicklistOptions: (picklistId?: Nullable<string>) => ReadonlyArray<PicklistOption>;
  selectPicklistItem: (index: number, insertionStart: PickListInsertionStart) => void;
};

const PicklistStoreContext = createContext<PicklistStoreBag | undefined>(undefined);
const PicklistEditorContext = createContext<PicklistEditorBag | undefined>(undefined);

const usePicklistInternal = (initialPicklists?: Picklists): PicklistBagInternal => {
  const [picklists, setPicklists] = useState<Picklists>(initialPicklists ?? []);
  const [selectedPicklistOptionIds, setSelectedPicklistOptionIds] =
    useState<SelectedPicklistOptionsMap>(() => {
      return (initialPicklists ?? []).reduce<SelectedPicklistOptionsMap>((acc, picklist) => {
        if (picklist.id != null && picklist.options.length > 0) {
          acc[picklist.id] = picklist.options[0]?.id;
        }
        return acc;
      }, {});
    });
  // Track the IDs of initialPicklists to detect when a new report is loaded
  const getInitialPicklistIds = (picklists: Picklists | undefined): string =>
    JSON.stringify(
      (picklists ?? [])
        .map((p) => p.id)
        .filter((id): id is string => id != null)
        .sort()
    );

  const previousInitialPicklistIdsRef = useRef<string>(getInitialPicklistIds(initialPicklists));

  // Sync picklists state when initialPicklists changes (e.g., when loading a new report)
  // Only sync if initialPicklists is non-empty to avoid resetting after stored picklists are merged
  useEffect(() => {
    if (initialPicklists == null || initialPicklists.length === 0) {
      return;
    }

    const currentInitialIds = getInitialPicklistIds(initialPicklists);

    // Only sync if the IDs in initialPicklists have changed (new report loaded)
    if (currentInitialIds !== previousInitialPicklistIdsRef.current) {
      setPicklists(initialPicklists);
      // Also update selectedPicklistOptionIds for new picklists
      setSelectedPicklistOptionIds((prev) => {
        const newOptions = initialPicklists.reduce<SelectedPicklistOptionsMap>((acc, picklist) => {
          if (picklist.id != null && picklist.options.length > 0) {
            // Keep existing selection if it exists, otherwise use first option
            acc[picklist.id] = prev[picklist.id] ?? picklist.options[0]?.id;
          }
          return acc;
        }, {});
        return { ...prev, ...newOptions };
      });
      previousInitialPicklistIdsRef.current = currentInitialIds;
    }
  }, [initialPicklists]);

  useEffect(() => {
    const selectedPicklistOptionIdKeys = Object.keys(selectedPicklistOptionIds);
    const validPicklists = picklists.filter((p) => p.id != null && p.options.length > 0);
    const uniqueValidPicklistIds = Array.from(new Set(validPicklists.map((p) => p.id as string)));
    const missingIds = uniqueValidPicklistIds.filter(
      (id) => !selectedPicklistOptionIdKeys.includes(id)
    );

    if (missingIds.length > 0) {
      setSelectedPicklistOptionIds((prev) => {
        const newOptions = missingIds.reduce<SelectedPicklistOptionsMap>((acc, id) => {
          const picklist = validPicklists.find((p) => p.id === id);
          if (picklist != null && picklist.options.length > 0) {
            acc[id] = prev[id] ?? picklist.options[0]?.id;
          }
          return acc;
        }, {});

        if (Object.keys(newOptions).length === 0) {
          return prev;
        }

        return {
          ...prev,
          ...newOptions,
        };
      });
    }
  }, [picklists, selectedPicklistOptionIds]);

  const insertPicklist = useCallback(({ picklistID }: { readonly picklistID: string }) => {
    setPicklists((picklists) => [...picklists, { id: picklistID, options: [] }]);
  }, []);
  const copyPicklist = useCallback(({ picklistID }: { readonly picklistID: string }) => {
    const copiedPicklist = { id: nanoid(), options: [] as ReportPicklistOption[] };

    setPicklists((prevPicklists) => {
      const foundPicklist =
        prevPicklists.find((p) => p.id === picklistID) ?? getPicklistFromClipboard(picklistID);
      copiedPicklist.options = [...(foundPicklist?.options ?? [])];

      setSelectedPicklistOptionIds((prev) => ({
        ...prev,
        [copiedPicklist.id]: copiedPicklist.options[0]?.id ?? '',
      }));

      return [...prevPicklists, copiedPicklist];
    });

    return copiedPicklist;
  }, []);

  const removePicklist = useCallback(({ picklistID }: { readonly picklistID: string }) => {
    setPicklists((picklists) => picklists.filter((picklist) => picklist.id !== picklistID));
    setSelectedPicklistOptionIds((prev) => {
      if (!(picklistID in prev)) {
        return prev;
      }

      const { [picklistID]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const updatePicklist = useCallback(
    ({
      picklistID,
      options,
    }: {
      options: Array<ReportPicklistOption>;
      readonly picklistID: string;
    }) => {
      setSelectedPicklistOptionIds((prev) => ({
        ...prev,
        [picklistID]: options.length > 0 ? options[0]?.id : '',
      }));

      setPicklists((picklists) => {
        if (picklists.find((p) => p.id === picklistID)) {
          return picklists.map((picklist) => {
            if (picklist.id === picklistID) {
              return { id: picklistID, options };
            }
            return picklist;
          });
        } else {
          return [...picklists, { id: picklistID, options }];
        }
      });
    },
    []
  );

  return {
    picklists,
    insertPicklist,
    copyPicklist,
    removePicklist,
    updatePicklist,
    setPicklists,
    selectedPicklistOptionIds,
    setSelectedPicklistOptionIds,
  };
};

const PicklistStoreProviderBase = ({
  children,
  initialPicklists,
  initialMacros = [] as Array<Macro>,
}: {
  children: React.ReactNode;
  initialPicklists?: Picklists;
  initialMacros?: Macros;
}): React.ReactElement => {
  const picklistBag = usePicklistInternal(initialPicklists);
  const { picklists, setPicklists } = picklistBag;
  const picklistsRef = useRef(picklists);
  const initialPicklistsRef = useRef(initialPicklists);
  const hasMergedStoredPicklistsRef = useRef(false);
  const [macros, setMacros] = useState<Macros>(initialMacros);

  useEffect(() => {
    // Track the latest picklist array so callbacks (e.g. copy handlers) always see current state
    // without rebuilding the callback on every change.
    picklistsRef.current = picklists;

    // Persist every change so the clipboard survives refreshes and other editors can hydrate.
    storeClipboardPicklists(picklists);
  }, [picklists]);

  // Track when initialPicklists change (e.g., when a new report is loaded)
  useEffect(() => {
    initialPicklistsRef.current = initialPicklists;
  }, [initialPicklists]);

  // On mount, merge any cached picklists (e.g. copies created pre-refresh) back into state.
  // However, if we have initialPicklists from a loaded report, those take precedence.
  useEffect(() => {
    if (hasMergedStoredPicklistsRef.current) {
      return;
    }

    const storedPicklists = getStoredPicklists();
    const initialPicklistsFromReport = initialPicklistsRef.current;

    if (storedPicklists.length === 0) {
      hasMergedStoredPicklistsRef.current = true;
      return;
    }

    // If we have initialPicklists from a loaded report (non-empty), only merge stored picklists that:
    // 1. Are not in the initial picklists (new/copied picklists)
    // 2. Don't conflict with initial picklists (initial picklists take precedence)
    // If initialPicklists is an empty array, treat it as "no initial picklists" and use original merge logic
    if (initialPicklistsFromReport != null && initialPicklistsFromReport.length > 0) {
      const initialPicklistIds = new Set(
        initialPicklistsFromReport.map((p) => p.id).filter((id): id is string => id != null)
      );

      // Only merge stored picklists that aren't in the initial picklists
      const picklistsToMerge = storedPicklists.filter(
        (storedPicklist) => storedPicklist.id != null && !initialPicklistIds.has(storedPicklist.id)
      );

      if (picklistsToMerge.length === 0) {
        hasMergedStoredPicklistsRef.current = true;
        return;
      }

      // Add stored picklists that aren't in initial picklists (these are likely copied picklists)
      setPicklists((prev) => {
        const existingIds = new Set(prev.map((p) => p.id).filter((id): id is string => id != null));
        const newPicklists = picklistsToMerge.filter((p) => p.id != null && !existingIds.has(p.id));
        if (newPicklists.length === 0) {
          return prev;
        }
        return [...prev, ...newPicklists];
      });
      hasMergedStoredPicklistsRef.current = true;
      return;
    }

    // If initialPicklists is undefined or empty array, use original merge logic
    // This handles the case where no report is loaded (new template) or empty initialPicklists
    // When initialPicklists is empty, we should always merge stored picklists
    const shouldMergeStoredPicklists =
      initialPicklistsFromReport == null ||
      initialPicklistsFromReport.length === 0 ||
      storedPicklists.some((storedPicklist) => {
        if (storedPicklist.id == null) return false;
        const existingPicklist = picklistsRef.current.find(
          (picklist) => picklist.id === storedPicklist.id
        );
        if (existingPicklist == null) return true;
        return !equals(existingPicklist, storedPicklist);
      });

    if (!shouldMergeStoredPicklists) {
      hasMergedStoredPicklistsRef.current = true;
      return;
    }

    // Stored picklists take precedence on hydration: drop local entries with the same ID,
    // then append the cached copies (so new or updated picklists survive across refreshes).
    setPicklists((prev) => {
      const next = prev.filter(
        (picklist) => !storedPicklists.some((storedPicklist) => storedPicklist.id === picklist.id)
      );
      return [...next, ...storedPicklists];
    });
    hasMergedStoredPicklistsRef.current = true;
  }, [setPicklists]);

  const cacheClipboardPicklists = useCallback((picklistIDs: readonly string[]) => {
    if (picklistIDs.length === 0) {
      return;
    }

    const picklistsToCache = picklistIDs
      .map((id) => picklistsRef.current.find((picklist) => picklist.id === id))
      .filter((picklist): picklist is Picklist => picklist != null);

    if (picklistsToCache.length === 0) {
      return;
    }

    storeClipboardPicklists(picklistsToCache);
  }, []);

  const copyPicklistWithPersistence = useCallback(
    (args: { readonly picklistID: string }) => {
      const newPicklist = picklistBag.copyPicklist(args);
      storeClipboardPicklists([newPicklist]);
      return newPicklist;
    },
    [picklistBag]
  );

  return (
    <PicklistStoreContext.Provider
      value={{
        ...picklistBag,
        macros,
        setMacros,
        copyPicklist: copyPicklistWithPersistence,
        cacheClipboardPicklists,
      }}
    >
      {children}
    </PicklistStoreContext.Provider>
  );
};

// PicklistStore manages picklist data and clipboard persistence without Slate dependencies.
export const PicklistStoreProvider = ({
  children,
  initialPicklists,
  initialMacros = [] as Array<Macro>,
}: {
  children: React.ReactNode;
  initialPicklists?: Picklists;
  initialMacros?: Macros;
}): React.ReactElement => {
  const picklistStore = useContext(PicklistStoreContext);

  // If a provider already exists up the tree, reuse it so nested providers share the same state.
  if (picklistStore != null) {
    return <>{children}</>;
  }

  return (
    <PicklistStoreProviderBase initialPicklists={initialPicklists} initialMacros={initialMacros}>
      {children}
    </PicklistStoreProviderBase>
  );
};

export const usePicklistStore = (): PicklistStoreBag => {
  const picklistBag = useContext(PicklistStoreContext);

  if (!picklistBag) {
    const errorMessage =
      '[usePicklist] usePicklistStore must be used within a PicklistStoreProvider.';
    const error = new Error(errorMessage);
    logger.error(errorMessage, error);
    throw error;
  }

  return picklistBag;
};

// PicklistEditor adds Slate-aware behaviors (active picklist, selection-based insertion) on top of PicklistStore.
export const PicklistEditorProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const picklistStoreBag = usePicklistStore();
  const { picklists, macros } = picklistStoreBag;
  const [activePicklist, setActivePicklist] = useState<Nullable<Picklist>>(null);
  const editor = useSlateStatic();
  const selection = useSlateSelection();
  const pluginsBag = usePlugins();
  const { onInsertTextRequiredField } = useRequiredFieldIndicator();

  useEffect(() => {
    sendCrossTabEvent(NameSpaces.ACTIVE_PICKLIST_DATA, {
      type: 'activePicklistData',
      data: {
        activePicklist,
        activePicklistOptionID:
          activePicklist == null
            ? ''
            : picklistStoreBag.selectedPicklistOptionIds[activePicklist?.id],
      },
    });
  }, [activePicklist, picklistStoreBag.selectedPicklistOptionIds]);

  // set activePicklist when picklists are loaded or selection changes
  useEffect(() => {
    if (editor == null) {
      return;
    }

    const currentSelection = selection ?? editor.selection;

    if (currentSelection != null) {
      const picklistEntry = getActivePicklist(
        editor,
        currentSelection
      ) as NodeEntry<PicklistPluginElement> | null;

      if (picklistEntry != null) {
        const [picklistNode] = picklistEntry;
        const picklistIdFromNode = picklistNode.picklistID;

        const foundPicklist = picklists.find((p) => p.id === picklistIdFromNode);

        if (foundPicklist != null) {
          const optionsChanged =
            activePicklist?.id === foundPicklist.id &&
            activePicklist.options.length !== foundPicklist.options.length;

          if (activePicklist?.id !== foundPicklist.id || optionsChanged) {
            setActivePicklist(foundPicklist);
          }
        }
      }
    }
  }, [editor, selection, picklists, activePicklist, setActivePicklist]);

  const selectPicklistItem = useCallback(
    (index: number, insertionStart: PickListInsertionStart) => {
      if (
        activePicklist == null ||
        editor == null ||
        pluginsBag == null ||
        editor.selection == null
      ) {
        const picklistErrorMessage =
          '[usePicklist] Could not select picklist item because no active picklist, selection, editor, or pluginsBag was found. Please try again.';
        logger.warn(picklistErrorMessage, {
          activePicklist,
          editor: editor != null ? partialEditor(editor) : 'null',
          pluginsBag: JSON.stringify(pluginsBag),
        });
        pickListInsertionMetrics.cancel(insertionStart);
        return;
      }
      const selection = editor.selection;

      const option = activePicklist.options[index];

      if (!option) {
        pickListInsertionMetrics.cancel(insertionStart);
        return;
      }

      // Use getActivePicklist to find the innermost picklist in nested scenarios
      const node = getActivePicklist(editor, selection) as NodeEntry<PicklistPluginElement> | null;

      if (!node) {
        pickListInsertionMetrics.cancel(insertionStart);
        return;
      }

      if (node[0].picklistID !== activePicklist.id) {
        pickListInsertionMetrics.cancel(insertionStart);
        return;
      }

      const insertionContext: PickListInsertionContext = {
        ...insertionStart,
        pickListId: activePicklist.id,
      };

      try {
        const unfurledPicklistContent = unfurlMacroPlaceholderStatic(
          option.text,
          macros,
          pluginsBag.getEditorStateEnhancers(),
          {
            fragmentMode: true,
          }
        );

        picklistStoreBag.setSelectedPicklistOptionIds((prev) => ({
          ...prev,
          [activePicklist.id]: option.id,
        }));

        insertSelectedPicklistItem(
          editor,
          node as unknown as NodeEntry<PicklistPluginElement & { children: any[] }>,
          unfurledPicklistContent,
          () => onInsertTextRequiredField(ReactEditor.findKey(editor, node[0])),
          { insertionContext }
        );
      } catch (error) {
        pickListInsertionMetrics.cancel(insertionStart);
        throw error;
      }
    },
    [activePicklist, editor, pluginsBag, macros, picklistStoreBag, onInsertTextRequiredField]
  );

  const getPicklistOptions = useCallback(
    (picklistId?: Nullable<string>): ReadonlyArray<PicklistOption> => {
      // Highest priority: if a specific picklist ID is provided, only return options for that picklist.
      if (picklistId != null) {
        const picklistOptions = picklists.find((picklist) => picklist.id === picklistId)?.options;
        return buildPicklistOptions(picklistOptions);
      }

      // Otherwise prefer the UI-selected active picklist when it has options.
      if ((activePicklist?.options?.length ?? 0) > 0) {
        return buildPicklistOptions(activePicklist?.options);
      }

      // Final fallback: flatten options from all known picklists.
      return picklists.flatMap((picklist) => buildPicklistOptions(picklist.options));
    },
    [activePicklist?.options, picklists]
  );

  const activePicklistOptions = useMemo(
    () => buildPicklistOptions(activePicklist?.options),
    [activePicklist?.options]
  );

  return (
    <PicklistEditorContext.Provider
      value={{
        ...picklistStoreBag,
        activePicklist,
        activePicklistOptions,
        getPicklistOptions,
        setActivePicklist,
        selectPicklistItem,
      }}
    >
      {children}
    </PicklistEditorContext.Provider>
  );
};

export const PicklistProvider = ({
  children,
  initialPicklists,
  initialMacros = [] as Array<Macro>,
}: {
  children: React.ReactNode;
  initialPicklists?: Picklists;
  initialMacros?: Macros;
}): React.ReactElement => {
  const picklistStore = useContext(PicklistStoreContext);

  // If a provider already exists up the tree, reuse it so nested providers share the same state.
  if (picklistStore != null) {
    return <PicklistEditorProvider>{children}</PicklistEditorProvider>;
  }

  return (
    <PicklistStoreProviderBase initialPicklists={initialPicklists} initialMacros={initialMacros}>
      <PicklistEditorProvider>{children}</PicklistEditorProvider>
    </PicklistStoreProviderBase>
  );
};

export const usePicklistEditor = (): PicklistEditorBag => {
  const picklistBag = useContext(PicklistEditorContext);

  if (!picklistBag) {
    const errorMessage = '[usePicklist] usePicklistEditor must be used within a PicklistProvider.';
    const error = new Error(errorMessage);
    logger.error(errorMessage, error);
    throw error;
  }

  return picklistBag;
};
